import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'

import { Icon } from '@/common/components/Icon'
import { PageTitle } from '@/components'
import { Button, Input, Modal } from '@/components/ui'
import FormGroup from '@/core/components/FormGroup'
import {
  createVendorTag,
  deleteVendorTag,
  IVendorTag,
  updateVendorTag,
} from '@/core/services/vendors'
import { DeleteConfirmationModal } from '@/home/components/DeleteConfirmationModal'

interface CreateTagModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  vendorId: string
  onSuccess: () => void
  onError: (message: string) => void
  tag?: IVendorTag | null
}

interface ITagFormValues {
  name: string
}

const schema = yup.object().shape({
  name: yup.string().required('Name is required'),
})

export const CreateTagModal = ({
  open,
  onOpenChange,
  vendorId,
  onSuccess,
  onError,
  tag,
}: CreateTagModalProps) => {
  const isEditMode = !!tag
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)

  const {
    handleSubmit,
    register,
    formState: { errors, dirtyFields, isSubmitting },
    reset,
    setValue,
  } = useForm<ITagFormValues>({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: { name: '' },
  })

  useEffect(() => {
    if (!open) return
    if (tag) {
      setValue('name', tag.name, { shouldValidate: true })
    }
    return () => {
      reset({ name: '' })
    }
  }, [open, tag])

  const handleClose = () => {
    onOpenChange(false)
  }

  const submit = async (values: ITagFormValues) => {
    try {
      if (isEditMode) {
        await updateVendorTag(vendorId, tag.id, { name: values.name })
      } else {
        await createVendorTag(vendorId, {
          id: '',
          name: values.name,
          createdAt: Date.now(),
        })
      }
      onSuccess()
    } catch (e) {
      if (e instanceof Error) onError(e.message)
    }
    handleClose()
  }

  const handleDeleteTag = async () => {
    if (!tag) return
    try {
      await deleteVendorTag(vendorId, tag.id)
      onSuccess()
    } catch (e) {
      if (e instanceof Error) onError(e.message)
    }
    handleClose()
  }

  return (
    <>
      <Modal
        open={open}
        onOpenChange={(o) => {
          if (!o) handleClose()
        }}
        size="xl"
        title={
          <div className="border-b border-monochrome-120 pb-4">
            <PageTitle
              icon="Vendor Entity"
              title={
                <span className="flex items-center gap-2">
                  {isEditMode ? 'Edit' : 'Add New'}{' '}
                  <span className="text-accent-1">Tag</span>
                </span>
              }
            />
          </div>
        }
        footer={
          <div className="flex w-full items-center justify-between gap-2">
            {isEditMode ? (
              <Button
                onClick={() => setDeleteModalOpen(true)}
                variant="destructive"
                className="mr-auto"
              >
                Delete Tag
                <Icon
                  icon="close-circle"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                />
              </Button>
            ) : (
              <div />
            )}
            <div className="flex items-center gap-2">
              <Button onClick={handleClose} type="button" variant="secondary">
                Cancel
              </Button>
              <Button
                disabled={
                  isSubmitting ||
                  (isEditMode && Object.keys(dirtyFields).length === 0)
                }
                form="create-tag-form"
                type="submit"
                variant="primary"
                loading={isSubmitting}
              >
                Save
              </Button>
            </div>
          </div>
        }
      >
        <form
          id="create-tag-form"
          onSubmit={handleSubmit(submit)}
          className="space-y-7"
        >
          <FormGroup label="Name" error={errors?.name?.message}>
            <Input
              isInvalid={!!errors?.name?.message}
              {...register('name')}
              type="text"
              placeholder="Enter Tag Name"
            />
          </FormGroup>
        </form>
      </Modal>

      <DeleteConfirmationModal
        showDeleteConfirmationModal={deleteModalOpen}
        setDeleteConfirmationModal={setDeleteModalOpen}
        deleteFunc={handleDeleteTag}
        deleteTitle="Tag"
        deletedItem={tag?.name}
      />
    </>
  )
}
